// Generate landing page copywriting from parsed book content.
// Output is structured data that Blitzscale pulls to populate product pages.

import type { ParsedBook, BookChapter } from './book-parser';
import { bookTitle, bookSubtitle, LEVEL_PRICES } from './book-parser';

export interface BookStats {
  chapter_count: number;
  subsection_count: number;
  vocab_domains: number;
  vocab_items: number;
  grammar_topics: number;
  mini_dialogs: number;
  sample_sentences: number;
  table_rows: number;
  has_romanization: boolean;
  has_cultural_notes: boolean;
  has_exercise_mapping: boolean;
  word_count: number;
}

export interface ChapterSummary {
  number: number;
  title: string;
  summary: string;
  subsection_count: number;
  has_tables: boolean;
  has_dialogs: boolean;
}

export interface BookCopywriting {
  title: string;
  subtitle: string;
  blurb: string;
  description: string;
  features: string[];
  chapter_summaries: ChapterSummary[];
  stats: BookStats;
  target_audience: string;
  prerequisites: string;
  what_you_will_learn: string[];
  price: number;
  bundle_price: number;
  seo_title: string;
  seo_description: string;
  keywords: string[];
}

const LEVEL_AUDIENCES: Record<string, { audience: string; prereqs: string }> = {
  A0: {
    audience: 'Absolute beginners who want to master the sound system before learning words and grammar.',
    prereqs: 'No prior knowledge required.',
  },
  A1: {
    audience: 'Complete beginners starting from zero. Ideal for travelers, language enthusiasts, or anyone curious about a new language.',
    prereqs: 'No prior knowledge required. A0 Phonics recommended but not essential.',
  },
  A2: {
    audience: 'Learners who know basic greetings and simple phrases and want to hold short conversations about everyday topics.',
    prereqs: 'Basic familiarity with the language (A1 level or equivalent).',
  },
  B1: {
    audience: 'Intermediate learners who can handle routine situations and want to express opinions, tell stories, and navigate social settings with confidence.',
    prereqs: 'Solid elementary foundation (A2 level or equivalent).',
  },
  B2: {
    audience: 'Upper-intermediate learners aiming to discuss complex topics, understand nuance, and communicate fluently in professional and social contexts.',
    prereqs: 'Comfortable with intermediate-level conversation (B1 level or equivalent).',
  },
  C1: {
    audience: 'Advanced learners pursuing near-native fluency — for academic study, professional use, or deep cultural engagement.',
    prereqs: 'Strong upper-intermediate skills (B2 level or equivalent).',
  },
  C2: {
    audience: 'Near-native speakers refining mastery — literary expression, regional nuance, idiomatic precision, and cultural depth.',
    prereqs: 'Advanced proficiency (C1 level or equivalent).',
  },
};

function countTableRows(content: string): number {
  const rows = content.match(/^\|[^-|].*\|$/gm) ?? [];
  const separators = content.match(/^\|[\s\-:|]+\|$/gm) ?? [];
  return Math.max(0, rows.length - separators.length);
}

function countSampleSentences(content: string): number {
  const tagged = content.match(/`\[(STD|COL|LIT|FML|INF)\]`/g) ?? [];
  const italicLines = content.match(/^\*[^*]+\*$/gm) ?? [];
  return tagged.length + Math.floor(italicLines.length / 2);
}

function countMiniDialogs(content: string): number {
  const blocks = content.split(/\n{2,}/);
  let count = 0;
  for (const block of blocks) {
    if (/^>\s/m.test(block) && block.split('\n').filter(l => l.startsWith('>')).length >= 2) {
      count++;
    }
  }
  return count;
}

function extractVocabDomainNames(book: ParsedBook): string[] {
  const names: string[] = [];
  for (const ch of book.chapters) {
    if (!ch.title.includes('§9') && !ch.title.includes('Vocabulary')) continue;
    for (const sub of ch.subsections) {
      const clean = sub.title
        .replace(/^(?:§\s*)?9\.\d+[\s.:,—-]*/, '')
        .replace(/\*\*/g, '')
        .trim();
      if (clean) names.push(clean);
    }
  }
  return names;
}

function extractGrammarTopicNames(book: ParsedBook): string[] {
  const names: string[] = [];
  for (const ch of book.chapters) {
    if (!ch.title.includes('§10') && !ch.title.includes('Grammar')) continue;
    for (const sub of ch.subsections) {
      const clean = sub.title
        .replace(/^(?:§\s*)?10\.\d+[\s.:,—-]*/, '')
        .replace(/\*\*/g, '')
        .trim();
      if (clean) names.push(clean);
    }
  }
  return names;
}

function computeStats(book: ParsedBook): BookStats {
  const fullContent = book.preamble + '\n' + book.chapters.map(c => c.content).join('\n');

  let vocabDomains = 0;
  let grammarTopics = 0;
  let hasCultural = false;
  let hasExercise = false;

  for (const ch of book.chapters) {
    if (ch.title.includes('§9') || ch.title.includes('Vocabulary')) {
      vocabDomains = ch.subsections.length;
    }
    if (ch.title.includes('§10') || ch.title.includes('Grammar')) {
      grammarTopics = ch.subsections.length;
    }
    if (ch.title.includes('§7') || ch.title.includes('Cultural')) {
      hasCultural = true;
    }
    if (ch.title.includes('§8') || ch.title.includes('Exercise')) {
      hasExercise = true;
    }
  }

  const words = fullContent.split(/\s+/).filter(w => w.length > 0).length;

  return {
    chapter_count: book.chapters.length,
    subsection_count: book.chapters.reduce((s, c) => s + c.subsections.length, 0),
    vocab_domains: vocabDomains,
    vocab_items: countTableRows(fullContent),
    grammar_topics: grammarTopics,
    mini_dialogs: countMiniDialogs(fullContent),
    sample_sentences: countSampleSentences(fullContent),
    table_rows: countTableRows(fullContent),
    has_romanization: !!book.metadata.romanization || /roman|transcri|pinyin|jyutping|hepburn/i.test(fullContent),
    has_cultural_notes: hasCultural,
    has_exercise_mapping: hasExercise,
    word_count: words,
  };
}

function generateBlurb(book: ParsedBook, stats: BookStats): string {
  const { languageName, level, levelName } = book.metadata;
  const parts: string[] = [];

  if (level === 'A0') {
    parts.push(`Master the sounds of ${languageName} before you learn a single word.`);
    parts.push(`This phonics guide covers the complete sound system — every vowel, consonant, and tone — with clear explanations and practice drills.`);
    return parts.join(' ');
  }

  parts.push(`Your complete ${levelName.toLowerCase()}-level guide to ${languageName}.`);

  const highlights: string[] = [];
  if (stats.vocab_domains > 0) highlights.push(`${stats.vocab_domains} vocabulary domains`);
  if (stats.grammar_topics > 0) highlights.push(`${stats.grammar_topics} grammar topics`);
  if (stats.mini_dialogs > 0) highlights.push(`${stats.mini_dialogs}+ mini-dialogs`);
  if (stats.vocab_items > 0) highlights.push(`${stats.vocab_items}+ vocabulary items`);

  if (highlights.length > 0) {
    parts.push(`Covers ${highlights.join(', ')}.`);
  }

  if (stats.has_romanization) {
    parts.push(`Includes full romanization and pronunciation guides.`);
  }
  if (stats.has_cultural_notes) {
    parts.push(`Cultural notes help you communicate appropriately in real situations.`);
  }

  return parts.join(' ');
}

function generateDescription(book: ParsedBook, stats: BookStats): string {
  const { languageName, level, levelName } = book.metadata;
  const lines: string[] = [];

  lines.push(`**Glossika ${languageName}: ${levelName}** is a structured, CEFR-aligned guide designed to take you from ${level === 'A1' ? 'zero' : `${level} level`} to confident communication.`);
  lines.push('');

  if (level === 'A0') {
    lines.push(`This phonics volume covers the complete sound inventory of ${languageName} — every phoneme, tone, and prosodic pattern — with systematic drills and clear explanations. Build a solid foundation before you tackle vocabulary and grammar.`);
    return lines.join('\n');
  }

  const vocabNames = extractVocabDomainNames(book);
  if (vocabNames.length > 0) {
    const displayed = vocabNames.slice(0, 8);
    lines.push(`**Vocabulary** spans ${stats.vocab_domains} real-world domains: ${displayed.join(', ')}${vocabNames.length > 8 ? `, and ${vocabNames.length - 8} more` : ''}. Each domain includes core word lists, number tables, situational phrases, and mini-dialogs you can use immediately.`);
    lines.push('');
  }

  const grammarNames = extractGrammarTopicNames(book);
  if (grammarNames.length > 0) {
    lines.push(`**Grammar** covers ${grammarNames.join(', ')} — explained through examples, not abstract rules. Every pattern comes with sample sentences showing how native speakers actually use it.`);
    lines.push('');
  }

  if (stats.has_cultural_notes) {
    lines.push(`**Cultural notes** prepare you for the social context behind the language — etiquette, customs, and the unwritten rules that textbooks usually skip.`);
    lines.push('');
  }

  if (stats.has_romanization) {
    lines.push(`**Pronunciation support** is built in: every example includes romanization so you can read and practice aloud from day one.`);
  }

  return lines.join('\n');
}

function generateFeatures(book: ParsedBook, stats: BookStats): string[] {
  const features: string[] = [];
  const { languageName, level } = book.metadata;

  features.push(`CEFR ${level} aligned — internationally recognized proficiency standard`);

  if (stats.vocab_domains > 0) {
    features.push(`${stats.vocab_domains} vocabulary domains with ${stats.vocab_items}+ words and phrases`);
  }
  if (stats.grammar_topics > 0) {
    features.push(`${stats.grammar_topics} grammar topics with clear explanations and examples`);
  }
  if (stats.mini_dialogs > 0) {
    features.push(`${stats.mini_dialogs}+ mini-dialogs showing natural conversation`);
  }
  if (stats.sample_sentences > 0) {
    features.push(`${stats.sample_sentences}+ sample sentences with register tags`);
  }
  if (stats.has_romanization) {
    features.push(`Full romanization and pronunciation guides`);
  }
  if (stats.has_cultural_notes) {
    features.push(`Cultural notes and etiquette guidance`);
  }
  if (stats.has_exercise_mapping) {
    features.push(`Exercise type mapping for self-study`);
  }

  features.push(`Written by Michael Campbell, founder of Glossika`);
  features.push(`Available in EPUB and PDF formats`);

  return features;
}

function generateWhatYouWillLearn(book: ParsedBook, stats: BookStats): string[] {
  const items: string[] = [];
  const { level } = book.metadata;

  const levelGoals: Record<string, string[]> = {
    A0: [
      'Recognize and produce every sound in the language',
      'Understand the writing system and its relationship to pronunciation',
      'Build a foundation for accurate pronunciation from the start',
    ],
    A1: [
      'Introduce yourself and make small talk',
      'Ask and answer simple questions about daily life',
      'Navigate basic social situations with confidence',
      'Express simple needs, preferences, and opinions',
      'Understand fundamental grammar patterns',
    ],
    A2: [
      'Describe past events and future plans',
      'Handle routine social interactions independently',
      'Compare and contrast things and ideas',
      'Manage telephone conversations and appointments',
      'Express agreement, disagreement, and preferences',
    ],
    B1: [
      'Express opinions with supporting arguments',
      'Tell stories and describe experiences in detail',
      'Handle unexpected situations while traveling or working',
      'Understand and use conditional language',
      'Navigate formal and informal registers appropriately',
    ],
    B2: [
      'Discuss complex topics fluently and spontaneously',
      'Understand idiomatic expressions and proverbs',
      'Write clear, detailed text on a wide range of subjects',
      'Interact with native speakers without strain for either party',
      'Appreciate literary and cultural references',
    ],
    C1: [
      'Express yourself fluently in social, academic, and professional settings',
      'Understand implicit meaning, humor, and cultural allusion',
      'Produce clear, well-structured text on complex subjects',
      'Use language flexibly for different purposes and audiences',
    ],
    C2: [
      'Express yourself with precision, subtlety, and nuance',
      'Understand virtually everything heard or read',
      'Appreciate literary language, dialect variation, and untranslatable concepts',
      'Command the full range of the language for any purpose',
    ],
  };

  items.push(...(levelGoals[level] ?? levelGoals['A1']!));

  const vocabNames = extractVocabDomainNames(book);
  if (vocabNames.length >= 3) {
    items.push(`Build vocabulary across ${vocabNames.length} real-world topics`);
  }

  return items;
}

function generateChapterSummaries(book: ParsedBook): ChapterSummary[] {
  return book.chapters.map(ch => {
    const hasT = /^\|/m.test(ch.content);
    const hasD = ch.subsections.some(s => s.hasMiniDialog);

    let summary = '';
    if (ch.title.includes('§1') || ch.title.includes('Exchanging Information')) {
      summary = 'Essential phrases for asking questions, giving directions, and sharing basic personal information.';
    } else if (ch.title.includes('§2') || ch.title.includes('Expressing Opinions')) {
      summary = 'Language for agreeing, disagreeing, and expressing your point of view.';
    } else if (ch.title.includes('§3') || ch.title.includes('Expressing Feelings')) {
      summary = 'Vocabulary and patterns for talking about emotions, preferences, and reactions.';
    } else if (ch.title.includes('§4') || ch.title.includes('Influencing Others')) {
      summary = 'How to make requests, give advice, suggest plans, and persuade.';
    } else if (ch.title.includes('§5') || ch.title.includes('Social Interaction')) {
      summary = 'Greetings, farewells, introductions, and the social glue of everyday conversation.';
    } else if (ch.title.includes('§6') || ch.title.includes('Discourse')) {
      summary = 'Connectors, fillers, and structuring language that makes your speech flow naturally.';
    } else if (ch.title.includes('§7') || ch.title.includes('Cultural')) {
      summary = 'Geography, daily life, celebrations, etiquette, and the cultural context behind the language.';
    } else if (ch.title.includes('§8') || ch.title.includes('Exercise')) {
      summary = 'A mapping of functional sections to language-specific drill types for self-study.';
    } else if (ch.title.includes('§9') || ch.title.includes('Vocabulary')) {
      summary = `${ch.subsections.length} themed vocabulary domains with word lists, number tables, situational phrases, and mini-dialogs.`;
    } else if (ch.title.includes('§10') || ch.title.includes('Grammar')) {
      summary = `${ch.subsections.length} grammar topics with paradigm tables, usage rules, and natural example sentences.`;
    } else {
      summary = `${ch.subsections.length} sections covering key language patterns and usage.`;
    }

    return {
      number: ch.number,
      title: ch.title,
      summary,
      subsection_count: ch.subsections.length,
      has_tables: hasT,
      has_dialogs: hasD,
    };
  });
}

function generateKeywords(book: ParsedBook): string[] {
  const { languageName, level, levelName } = book.metadata;
  const kw = [
    `learn ${languageName}`,
    `${languageName} grammar`,
    `${languageName} vocabulary`,
    `CEFR ${level}`,
    `${languageName} ${levelName.toLowerCase()}`,
    `${languageName} textbook`,
    `${languageName} self-study`,
    'Glossika',
    'language learning',
    `${languageName} ebook`,
  ];
  if (book.metadata.script) kw.push(`${languageName} script`);
  if (level === 'A0') kw.push(`${languageName} pronunciation`, `${languageName} phonics`);
  if (level === 'A1') kw.push(`${languageName} for beginners`, `start learning ${languageName}`);
  return kw;
}

export function generateBookCopywriting(book: ParsedBook): BookCopywriting {
  const stats = computeStats(book);
  const { languageName, level, levelName } = book.metadata;
  const title = bookTitle(languageName, levelName);
  const subtitle = bookSubtitle(levelName);
  const levelInfo = LEVEL_AUDIENCES[level] ?? LEVEL_AUDIENCES['A1']!;

  return {
    title,
    subtitle: `${subtitle} — CEFR ${level}`,
    blurb: generateBlurb(book, stats),
    description: generateDescription(book, stats),
    features: generateFeatures(book, stats),
    chapter_summaries: generateChapterSummaries(book),
    stats,
    target_audience: levelInfo.audience,
    prerequisites: levelInfo.prereqs,
    what_you_will_learn: generateWhatYouWillLearn(book, stats),
    price: LEVEL_PRICES[level] ?? 3,
    bundle_price: 20,
    seo_title: `${title}: ${subtitle} (CEFR ${level}) — Glossika`,
    seo_description: `Learn ${languageName} with Glossika's ${levelName} guide. ${stats.vocab_items}+ vocabulary items, ${stats.grammar_topics} grammar topics, mini-dialogs, and cultural notes. CEFR ${level} aligned. EPUB & PDF.`,
    keywords: generateKeywords(book),
  };
}

export { computeStats, extractVocabDomainNames, extractGrammarTopicNames };
